import {
  buildToolSelectionDetailsPayload,
  getToolTrackingValues,
  setToolTrackingValues,
  syncToolSelectionCartId,
  type ToolSelectionCartType
} from '~/utils/toolSelectionTracking/selectionDetails';
import type { LightingAnswers } from '~/types/lightingAdvisor';
import type { LightingAdvisorDecoration } from '~/utils/lightingAdvisor/catalogTypes';

const LA_TOOL_KEY = 'lighting_advisor';
const LA_EVENT = 'lightingAdvisor';
const LA_CATEGORY = 'Lighting Advisor';

export type LightingAdvisorTrackingContext = {
  answers: LightingAnswers;
  resultCount: number;
  decorations: LightingAdvisorDecoration[];
};

export function buildLightingAdvisorTrackingAnswers(
  answers: LightingAnswers
): Record<string, string> {
  const payload: Record<string, string> = {};
  const objectSize = [answers.a2s2q2_length, answers.a2s2q2_width].filter(
    (value): value is string => Boolean(value)
  );

  for (const [questionId, value] of Object.entries(answers)) {
    if (
      value == null ||
      value === '' ||
      questionId === 'a2s2q2_length' ||
      questionId === 'a2s2q2_width' ||
      questionId === 'a2s5q2_basler_series'
    ) {
      continue;
    }
    if (questionId === 'a2s5q2_basler_camera') {
      payload.camera_model = value;
    } else if (questionId === 'a2s5q2_thirdparty_mount') {
      payload.lens_mount = value;
    } else if (questionId === 'a2s5q2_thirdparty_monocolor') {
      payload.monocolor = value;
    } else {
      payload[questionId] = value;
    }
  }

  if (objectSize.length) {
    payload.a2s2q2 = objectSize.join('x');
  }
  return payload;
}

function orderNumbersByFlag(
  decorations: LightingAdvisorDecoration[],
  identifiers: readonly string[]
): string[] {
  return decorations
    .filter(
      (decoration) =>
        decoration.type === 'flag' &&
        identifiers.includes(decoration.identifier) &&
        decoration.ordernumber
    )
    .map((decoration) => String(decoration.ordernumber));
}

export function buildLightingAdvisorTrackingContext(
  context: LightingAdvisorTrackingContext,
  options?: { zeroResults?: boolean }
): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    ...buildLightingAdvisorTrackingAnswers(context.answers),
    lightsinresult: options?.zeroResults ? 0 : String(context.resultCount)
  };

  if (!options?.zeroResults) {
    payload.technicalfit = orderNumbersByFlag(context.decorations, [
      'best_fit',
      'best_technical_fit'
    ]).join(', ');
    payload.priceperformance = orderNumbersByFlag(context.decorations, [
      'price_performance_ratio'
    ]).join(', ');
  }
  return payload;
}

export function buildLightingAdvisorEvent(
  action: string,
  label: string | Record<string, unknown>,
  options?: { category?: string; event?: string }
): {
  event: string;
  eventInfo: EventInfoModel;
} {
  return {
    event: options?.event ?? LA_EVENT,
    eventInfo: {
      category: options?.category ?? LA_CATEGORY,
      action,
      label: typeof label === 'string' ? label : JSON.stringify(label)
    }
  };
}

export function useLightingAdvisorAnalytics() {
  const { $analytics } = useNuxtApp();

  function trackEvent(
    action: string,
    label: string | Record<string, unknown> = '',
    options?: { category?: string; event?: string }
  ): void {
    if (!import.meta.client) {
      return;
    }
    $analytics.pushToDataLayer(
      buildLightingAdvisorEvent(action, label, options)
    );
  }

  function trackStepExplanation(stepLabel: string): void {
    trackEvent('click', `explanationdetails_${stepLabel}`);
  }

  function trackQuestionInformation(
    stepLabel: string,
    questionLabel: string
  ): void {
    trackEvent('click', `information_${stepLabel}_${questionLabel}`);
  }

  function trackStepNavigation(stepLabel: string): void {
    trackEvent('navigation', stepLabel);
  }

  function trackJumpToResults(): void {
    trackEvent('click', 'gotolighting_btn');
  }

  function trackResultsContext(context: LightingAdvisorTrackingContext): void {
    const payload = buildLightingAdvisorTrackingContext(context, {
      zeroResults: context.resultCount === 0
    });
    if (context.resultCount === 0) {
      trackEvent('zeroresults', payload);
      return;
    }
    setToolTrackingValues(LA_TOOL_KEY, payload);
  }

  function trackProductSelect(model: string, sku: string): void {
    const label = `${model} (${sku})`;
    trackEvent('productselection', label);
    trackEvent('select_btn', {
      ...getToolTrackingValues(LA_TOOL_KEY),
      selectedProduct: label,
      selectedacc: ''
    });
  }

  function trackWarningDetails(): void {
    trackEvent('click', 'warningdetails');
  }

  function trackSupport(area: 'Results List' | 'Overview Page'): void {
    trackEvent('support_btn', area);
  }

  function trackSelectionDetails(params: {
    selectedItem: string;
    selectedItemId: string;
    selectedAccessorySkus: string[];
    type: ToolSelectionCartType;
    cartId?: string;
  }): void {
    if (!import.meta.client) {
      return;
    }
    if (params.cartId && params.type !== 'add_to_config') {
      syncToolSelectionCartId(
        params.type === 'req' ? 'requestCart' : 'cart',
        params.cartId
      );
    }
    setToolTrackingValues(LA_TOOL_KEY, {
      selected_item: params.selectedItem,
      selected_item_id: params.selectedItemId,
      selectedacc: params.selectedAccessorySkus.join(', ')
    });
    $analytics.pushToDataLayer(
      buildToolSelectionDetailsPayload(
        getToolTrackingValues(LA_TOOL_KEY),
        params.type
      )
    );
  }

  return {
    trackEvent,
    trackStepExplanation,
    trackQuestionInformation,
    trackStepNavigation,
    trackJumpToResults,
    trackResultsContext,
    trackProductSelect,
    trackWarningDetails,
    trackSupport,
    trackSelectionDetails
  };
}
